import { Minus, Plus, ShoppingBag } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Seo } from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { useBasket } from "@/context/BasketContext";
import { useSite } from "@/context/SiteContext";
import { API, errMsg, imgUrl, money } from "@/lib/api";

export default function ProductDetail() {
  const { slug } = useParams();
  const { categories, storeOpen, settings } = useSite();
  const basket = useBasket();
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [imgIdx, setImgIdx] = useState(0);
  const [variation, setVariation] = useState(null);
  const [addons, setAddons] = useState([]);
  const [qty, setQty] = useState(1);

  useEffect(() => {
    setProduct(null);
    setNotFound(false);
    setVariation(null);
    setAddons([]);
    setQty(1);
    setImgIdx(0);
    API.get(`/products/${slug}`)
      .then((r) => setProduct(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  const category = useMemo(
    () => (product ? categories.find((c) => c.id === product.category_id) : null),
    [product, categories]
  );

  const unit = useMemo(() => {
    if (!product) return 0;
    if (variation) return Number(variation.price);
    return Number(product.sale_price || product.price);
  }, [product, variation]);

  const total = (unit + addons.reduce((s, a) => s + Number(a.price), 0)) * qty;

  const addToBasket = () => {
    basket.add(product, { variation, addons, quantity: qty });
    toast.success(`${product.name} added to your basket`, {
      action: { label: "View basket", onClick: () => (window.location.href = "/basket") },
    });
  };

  if (notFound) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-24 text-center" data-testid="product-not-found">
        <h1 className="font-display text-3xl">Product not found</h1>
        <Button asChild className="mt-6 rounded-full"><Link to="/shop">Back to shop</Link></Button>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-14 grid md:grid-cols-2 gap-10">
        <Skeleton className="aspect-[4/5] rounded-xl" />
        <div className="space-y-4 pt-4"><Skeleton className="h-10 w-2/3" /><Skeleton className="h-6 w-1/3" /><Skeleton className="h-32 w-full" /></div>
      </div>
    );
  }

  const images = product.images || [];
  const stock = product.stock ?? null;
  const canOrder = storeOpen && product.in_stock && (stock === null || stock > 0);

  return (
    <div className="max-w-7xl mx-auto px-4 py-10 sm:py-14" data-testid="product-detail-page">
      <Seo title={`${product.name} | Eileen Jenkins Florist`} description={product.description?.slice(0, 155)} />
      <nav className="text-sm text-muted-foreground mb-6" aria-label="Breadcrumb">
        <Link to="/shop" className="hover:text-primary">Shop</Link>
        {category && <> / <Link to={`/shop?category=${category.slug}`} className="hover:text-primary">{category.name}</Link></>}
        {" / "}<span className="text-foreground">{product.name}</span>
      </nav>

      <div className="grid md:grid-cols-2 gap-10 lg:gap-16">
        <div>
          <div className="rounded-xl overflow-hidden bg-white aspect-[4/5]" data-testid="product-main-image">
            {images[imgIdx] && <img src={imgUrl(images[imgIdx])} alt={product.name} className="h-full w-full object-contain" />}
          </div>
          {images.length > 1 && (
            <div className="flex gap-3 mt-3" data-testid="product-thumbs">
              {images.map((im, i) => (
                <button
                  key={i}
                  onClick={() => setImgIdx(i)}
                  data-testid={`product-thumb-${i}`}
                  className={`rounded-lg overflow-hidden w-20 aspect-square bg-white border-2 transition-colors ${i === imgIdx ? "border-primary" : "border-transparent"}`}
                >
                  <img src={imgUrl(im)} alt="" className="h-full w-full object-contain" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <h1 className="font-display text-3xl sm:text-4xl tracking-tight" data-testid="product-name">{product.name}</h1>
          <p className="mt-3 text-xl font-semibold text-primary" data-testid="product-price">{money(unit)}</p>

          <p className="mt-6 text-muted-foreground leading-relaxed whitespace-pre-line" data-testid="product-description">{product.description}</p>

          {(product.variations || []).length > 0 && (
            <div className="mt-8">
              <Label className="text-sm font-semibold">Options</Label>
              <div className="flex flex-wrap gap-2 mt-2" data-testid="variation-options">
                {product.variations.map((v) => (
                  <button
                    key={v.name}
                    onClick={() => setVariation(variation?.name === v.name ? null : v)}
                    data-testid={`variation-${v.name.replace(/\s+/g, "-").toLowerCase()}`}
                    className={`px-4 py-2 rounded-full border text-sm transition-colors ${variation?.name === v.name ? "bg-primary text-primary-foreground border-primary" : "hover:border-primary"}`}
                  >
                    {v.name} · {money(v.price)}
                  </button>
                ))}
              </div>
            </div>
          )}

          {(product.addons || []).length > 0 && (
            <div className="mt-6">
              <Label className="text-sm font-semibold">Add something extra</Label>
              <div className="space-y-2 mt-2" data-testid="addon-options">
                {product.addons.map((a) => (
                  <label key={a.name} className="flex items-center gap-3 text-sm cursor-pointer" data-testid={`addon-${a.name.replace(/\s+/g, "-").toLowerCase()}`}>
                    <Checkbox
                      checked={addons.some((x) => x.name === a.name)}
                      onCheckedChange={(checked) =>
                        setAddons((prev) => (checked ? [...prev, a] : prev.filter((x) => x.name !== a.name)))
                      }
                    />
                    <span>{a.name}{Number(a.price) > 0 ? ` (+${money(a.price)})` : " (free)"}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="mt-8 flex items-center gap-4">
            <div className="flex items-center border rounded-full">
              <button onClick={() => setQty((v) => Math.max(1, v - 1))} className="p-3" data-testid="qty-decrease-btn" aria-label="Decrease quantity"><Minus className="h-4 w-4" /></button>
              <span className="w-8 text-center font-medium" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty((v) => Math.min(stock ?? 50, v + 1))} className="p-3" data-testid="qty-increase-btn" aria-label="Increase quantity"><Plus className="h-4 w-4" /></button>
            </div>
            <Button
              size="lg"
              className="rounded-full flex-1 sm:flex-none px-8"
              onClick={addToBasket}
              disabled={!canOrder}
              data-testid="add-to-basket-btn"
            >
              <ShoppingBag className="mr-2 h-4 w-4" /> Add to basket · {money(total)}
            </Button>
          </div>

          {!storeOpen && (
            <p className="mt-4 text-sm text-amber-700 dark:text-amber-400" data-testid="product-store-closed-note">
              {settings?.closed_message || "Online ordering is currently closed."}
            </p>
          )}
          {storeOpen && (!product.in_stock || stock === 0) && (
            <p className="mt-4 text-sm text-muted-foreground" data-testid="product-out-of-stock-note">This product is currently out of stock.</p>
          )}
          {storeOpen && stock !== null && stock > 0 && stock <= 5 && (
            <p className="mt-4 text-sm font-medium text-amber-600" data-testid="product-low-stock-note">Only {stock} left in stock — order soon.</p>
          )}
          {storeOpen && product.in_stock && Number(settings?.delivery?.min_order) > 0 && (
            <p className="mt-4 text-xs text-muted-foreground" data-testid="product-min-order-note">
              Online orders have a {money(settings.delivery.min_order)} minimum.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
